import React from "react";
import { Button } from "reactstrap";
import Select from "react-select";
import NavHeader from "./NavHeader";
import NavRow from "./NavRow";
import { etiquetaFecha, numerosDeFecha, ordenarFechasDesc } from "./fechas";
import { toastConfirm } from "components/utils/toasts";
import { contar, enumerar, jugados, textoArrastre } from "./bajas";

// Una zona: nombre + equipos asignados + lista de fechas.
// takenByOtherZones: ids de equipos ya usados por otras zonas de la misma rueda.
//
// El nombre y los equipos de la zona son estructura y se guardan con el torneo
// entero (onChange). El alta y la baja de una fecha, y sacar un equipo, los resuelve
// el host con un guardado granular, porque se llevan por delante lo que otros puedan
// estar cargando de esas fechas: por eso van por callbacks propios.
export default function ZoneScreen({
  zone,
  teams,
  takenByOtherZones = [],
  onChange,
  onRemoveTeam,
  onAddDate,
  onRemoveDate,
  onOpenDate,
  onBack,
}) {
  const getTeamName = (teamId) => {
    const t = teams.find((x) => x.id === teamId);
    return t ? t.name : "Desconocido";
  };

  const rename = (name) => onChange({ ...zone, name });

  const availableOptions = teams
    .filter((t) => !zone.teamIds.includes(t.id) && !takenByOtherZones.includes(t.id))
    .map((t) => ({ value: t.id, label: t.name }));

  const addTeam = (option) => {
    if (!option || zone.teamIds.includes(option.value)) return;
    onChange({ ...zone, teamIds: [...zone.teamIds, option.value] });
  };

  // Al sacar un equipo de la zona, se limpian su lista de fe y los partidos que lo
  // incluyan (con el resultado que tuvieran cargado). Se confirma antes; la limpieza
  // la hace el host.
  const removeTeam = (teamId) => {
    const enElEquipo = (m) => m.homeTeamId === teamId || m.awayTeamId === teamId;
    const partidos = zone.matchDates.flatMap((md) => (md.matches || []).filter(enElEquipo));
    const detalle = enumerar([
      partidos.length ? contar(partidos.length, "partido", "partidos") : "",
      jugados(partidos) ? contar(jugados(partidos), "resultado ya cargado", "resultados ya cargados") : "",
    ]);
    const texto = detalle
      ? "Se eliminan " + detalle + ", y sale de la lista de fe de las fechas."
      : "Todavía no tiene partidos en esta zona.";

    toastConfirm("¿ Sacar a " + getTeamName(teamId) + " de la zona ?", texto, () => onRemoveTeam(teamId));
  };

  // Borrar una fecha se lleva sus partidos y los resultados que tuvieran cargados.
  const removeDate = (md, numero) => {
    const partidos = md.matches || [];
    const texto =
      textoArrastre({ partidos: partidos.length, resultados: jugados(partidos) }) ||
      "La fecha todavía no tiene partidos.";

    toastConfirm("¿ Eliminar la " + etiquetaFecha(md, numero, "corto") + " ?", texto, () => onRemoveDate(md.id));
  };

  // El número de fecha se calcula sobre toda la zona (no sobre la posición en la
  // lista) para que coincida con el que ven los demás listados. El listado va de
  // la última fecha a la primera.
  const numeros = numerosDeFecha(zone.matchDates);
  const fechasDesc = ordenarFechasDesc(zone.matchDates);

  return (
    <div className="screen-slide">
      <NavHeader title={zone.name} subtitle={zone.teamIds.length + " equipos"} onBack={onBack} />

      <div className="card mb-3">
        <div className="card-body">
          <div className="form-group mb-0">
            <label className="font-weight-bold" style={{ fontSize: "0.875rem" }}>
              Nombre de la Zona
            </label>
            <input
              type="text"
              className="form-control form-control-lg"
              value={zone.name}
              onChange={(e) => rename(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="card mb-3">
        <div className="card-body">
          <label className="font-weight-bold" style={{ fontSize: "0.875rem" }}>
            Agregar Equipos a la Zona
          </label>
          <Select
            isSearchable={true}
            placeholder="Seleccioná un equipo..."
            classNamePrefix="react-select"
            name="addTeamToZone"
            value={null}
            onChange={addTeam}
            options={availableOptions}
            noOptionsMessage={() => "Todos los equipos asignados"}
          />

          {zone.teamIds.length > 0 && (
            <div className="d-flex flex-wrap mt-3" style={{ gap: "0.375rem" }}>
              {zone.teamIds.map((teamId) => (
                <span key={teamId} className="player-chip selected">
                  {getTeamName(teamId)}
                  <button
                    type="button"
                    className="remove-btn"
                    onClick={() => removeTeam(teamId)}
                    aria-label={"Sacar " + getTeamName(teamId)}
                  >
                    x
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="d-flex align-items-center justify-content-between mb-2">
        <div className="sub-section-title mb-0">Fechas</div>
        <Button className="btn-arhock btn-sm my-0" onClick={onAddDate} disabled={zone.teamIds.length < 2}>
          + Agregar Fecha
        </Button>
      </div>

      {zone.teamIds.length < 2 ? (
        <p className="text-muted" style={{ fontSize: "0.8125rem" }}>
          Debe haber al menos 2 equipos en la zona para programar fechas.
        </p>
      ) : zone.matchDates.length === 0 ? (
        <p className="text-muted text-center py-2" style={{ fontSize: "0.875rem" }}>
          No hay fechas creadas. Tocá &quot;Agregar Fecha&quot; para empezar a programar partidos.
        </p>
      ) : (
        fechasDesc.map((md) => {
          const asistentes = Object.values(md.attendance || {}).reduce((s, arr) => s + arr.length, 0);
          return (
            <NavRow
              key={md.id}
              title={etiquetaFecha(md, numeros[md.id], "corto")}
              accent="date"
              badges={[
                { label: md.matches.length + " partidos", variant: "dates" },
                { label: asistentes + " lista de fe", variant: "teams" },
              ]}
              onClick={() => onOpenDate(md.id)}
              onDelete={() => removeDate(md, numeros[md.id])}
            />
          );
        })
      )}
    </div>
  );
}
